// Synchroniseur hors connexion (H3, architecture section 8, cadrage section 9). Client générique, sans écran :
// - enregistrer(type, charge) : l'opération est chiffrée dans la file persistante (« enregistré sur cet appareil »),
//   puis « en attente d'envoi » dès que le réseau est présent ;
// - synchroniser() : envoi par lots à POST /api/v1/sync ; l'opération passe « envoyé, en attente d'accusé », puis
//   « synchronisé » seulement à l'accusé du serveur (acceptée ou déjà reçue), « conflit à résoudre » ou « refusé »
//   avec le motif ; une coupure pendant l'envoi la remet « en attente d'envoi » (le serveur est idempotent) ;
// - reprise automatique à l'ouverture et au retour du réseau ; paquet de travail versionné (GET /api/v1/sync/paquet),
//   conservé chiffré ; détection d'une purge du stockage ; expiration locale de la session à 24 h ; purge des
//   refus au-delà de 30 jours.
// Les écrans du lot 3 s'y raccordent par le contexte React (`contexte.tsx`) ; le lot 6 (inspections) aussi.

import { CONSERVATION_REFUS_JOURS, EXPIRATION_SESSION_LOCALE_HEURES } from '@carburanttrack/shared/logistique';
import type { FileHorsLigne, OperationStockee } from './file';

export interface OperationEnvoi {
  identifiantClient: string;
  type: string;
  charge: unknown;
  horodatageEvenement: string;
  horodatageLocal: string;
  versionObjet?: string | null;
}

export interface ResultatServeur {
  identifiantClient: string;
  etat: 'acceptee' | 'deja_recue' | 'refusee' | 'conflit';
  code?: string;
  motif?: string;
  resultat?: unknown;
  objet?: { type: string; id: string; version?: string };
  conflitId?: string;
  versions?: { client: unknown; serveur: unknown };
  /** Conflit tranché par un validateur depuis le dernier envoi : version retenue et motif. */
  resolution?: { versionRetenue: 'client' | 'serveur'; motif: string };
}

export interface PaquetServeur {
  inchange: boolean;
  version: string;
  genereLe?: string;
  contenu?: Record<string, unknown>;
}

/** Accès au serveur, injecté (tests) ; par défaut, l'API de l'application. */
export interface TransportSynchro {
  envoyer(operations: OperationEnvoi[]): Promise<ResultatServeur[]>;
  paquet(versionConnue: string | null): Promise<PaquetServeur>;
}

export interface StockageLocal {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

export interface BilanSynchro {
  envoyees: number;
  synchronisees: number;
  conflits: number;
  refusees: number;
  /** Envoi interrompu (réseau, session) : les opérations restent en attente. */
  interrompu: string | null;
}

export interface EtatSynchro {
  enLigne: boolean;
  enCours: boolean;
  derniereSynchro: string | null;
  derniereErreur: string | null;
  compteurs: Record<OperationStockee['etat'], number>;
  /** Enregistrements locaux perdus (purge du stockage par le système), non encore pris en compte. */
  perdus: string[];
  sessionExpiree: boolean;
  paquetVersion: string | null;
  paquetLe: string | null;
}

export interface OptionsSynchroniseur {
  file: FileHorsLigne;
  transport: TransportSynchro;
  /** Stockage durable des métadonnées (dernière validation de session, versions) : localStorage. */
  stockage: StockageLocal;
  enLigne?: () => boolean;
  maintenant?: () => Date;
  tailleLot?: number;
}

const CLE_SESSION = 'ct-session-locale';
const CLE_DERNIERE = 'ct-derniere-synchro';
const CLE_PAQUET = 'paquet';
const H = 3_600_000;
// Les opérations en conflit sont renvoyées aussi : le serveur, idempotent, répond « déjà reçue » avec le conflit
// tant qu'il est ouvert, puis avec la résolution une fois tranché par un validateur ; l'appareil en est informé.
const ETATS_A_ENVOYER: OperationStockee['etat'][] = ['enregistre_local', 'en_attente_envoi', 'envoye', 'conflit'];
export const MOTIF_CHARGE_ILLISIBLE = 'Enregistrement illisible sur cet appareil : la clé de session a été perdue (application fermée avant l’envoi). Saisissez-le de nouveau.';

export class ErreurTransport extends Error {
  constructor(
    message: string,
    public readonly sessionPerdue = false,
  ) {
    super(message);
  }
}

/**
 * Préparation d'une charge juste avant son envoi (photos prises sans réseau envoyées d'abord, par exemple).
 * Une `ErreurTransport` interrompt l'envoi (l'opération reste en attente) ; toute autre erreur refuse l'opération
 * avec son message pour motif.
 */
export type PreparationCharge = (charge: unknown, operation: OperationStockee) => Promise<unknown>;

export class Synchroniseur {
  private readonly file: FileHorsLigne;
  private readonly transport: TransportSynchro;
  private readonly stockage: StockageLocal;
  private readonly estEnLigne: () => boolean;
  private readonly maintenant: () => Date;
  private readonly tailleLot: number;
  private readonly preparations = new Map<string, PreparationCharge>();
  private ecouteurs = new Set<(e: EtatSynchro) => void>();
  private enCours: Promise<BilanSynchro> | null = null;
  private etatCourant: EtatSynchro;
  private nettoyage: (() => void) | null = null;

  constructor(o: OptionsSynchroniseur) {
    this.file = o.file;
    this.transport = o.transport;
    this.stockage = o.stockage;
    this.estEnLigne = o.enLigne ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
    this.maintenant = o.maintenant ?? (() => new Date());
    this.tailleLot = o.tailleLot ?? 50;
    this.etatCourant = {
      enLigne: this.estEnLigne(),
      enCours: false,
      derniereSynchro: this.stockage.getItem(CLE_DERNIERE),
      derniereErreur: null,
      compteurs: { enregistre_local: 0, en_attente_envoi: 0, envoye: 0, synchronise: 0, conflit: 0, refuse: 0 },
      perdus: [],
      sessionExpiree: false,
      paquetVersion: null,
      paquetLe: null,
    };
  }

  // --- Session locale (24 h) ------------------------------------------------------------------------------

  /** Le serveur vient de confirmer la session : l'application peut s'ouvrir hors connexion pendant 24 h. */
  validerSession(utilisateurId: string): void {
    this.stockage.setItem(CLE_SESSION, JSON.stringify({ utilisateurId, valideeLe: this.maintenant().toISOString() }));
    if (this.etatCourant.sessionExpiree) this.publier({ sessionExpiree: false });
  }

  /** Vrai si la dernière validation par le serveur date de plus de 24 h (ou n'existe pas). */
  sessionLocaleExpiree(utilisateurId?: string): boolean {
    const brut = this.stockage.getItem(CLE_SESSION);
    if (!brut) return true;
    try {
      const s = JSON.parse(brut) as { utilisateurId: string; valideeLe: string };
      if (utilisateurId && s.utilisateurId !== utilisateurId) return true;
      return this.maintenant().getTime() - Date.parse(s.valideeLe) > EXPIRATION_SESSION_LOCALE_HEURES * H;
    } catch {
      return true;
    }
  }

  /** Fin de session (déconnexion, compte suspendu) : la validation locale est oubliée. */
  oublierSession(): void {
    this.stockage.removeItem(CLE_SESSION);
    this.publier({ sessionExpiree: true });
  }

  // --- File ---------------------------------------------------------------------------------------------------

  /**
   * Enregistre une opération de terrain. Elle est d'abord « enregistrée sur cet appareil » ; si le réseau est
   * présent, elle passe « en attente d'envoi » et une synchronisation est lancée sans être attendue.
   */
  async enregistrer(type: string, charge: unknown, options: { horodatageEvenement?: Date; versionObjet?: string | null; resume?: string | null } = {}): Promise<OperationStockee> {
    const op = await this.file.ajouter(type, charge, options);
    if (this.estEnLigne()) {
      await this.file.changerEtat(op.identifiantClient, 'en_attente_envoi');
      void this.synchroniser().catch(() => undefined);
    }
    await this.rafraichir();
    return (await this.file.lire(op.identifiantClient)) ?? op;
  }

  async operations(): Promise<(OperationStockee & { libelle: string | null })[]> {
    const ops = await this.file.lister();
    return Promise.all(ops.map(async (o) => ({ ...o, libelle: await this.file.resume(o) })));
  }

  async charge<T>(identifiantClient: string): Promise<T> {
    return this.file.charge<T>(identifiantClient);
  }

  /** Déclare la préparation des charges d'un type d'opération (un module par type ; la dernière déclarée vaut). */
  preparer(type: string, preparation: PreparationCharge | null): void {
    if (preparation) this.preparations.set(type, preparation);
    else this.preparations.delete(type);
  }

  /**
   * Envoi par lots. Une seule synchronisation à la fois (un appel concurrent reçoit la même promesse). Les
   * opérations « envoyé » sans accusé (coupure, fermeture de la page) sont renvoyées : le serveur rend la
   * réponse initiale sans rejouer (idempotence par l'identifiant client, R-05).
   */
  synchroniser(): Promise<BilanSynchro> {
    if (this.enCours) return this.enCours;
    this.enCours = this.executer().finally(() => {
      this.enCours = null;
      void this.rafraichir({ enCours: false });
    });
    return this.enCours;
  }

  private async executer(): Promise<BilanSynchro> {
    const bilan: BilanSynchro = { envoyees: 0, synchronisees: 0, conflits: 0, refusees: 0, interrompu: null };
    if (!this.estEnLigne()) {
      bilan.interrompu = 'Hors connexion';
      await this.rafraichir({ enLigne: false });
      return bilan;
    }
    await this.rafraichir({ enLigne: true, enCours: true, derniereErreur: null });
    const aEnvoyer = (await this.file.lister()).filter((o) => ETATS_A_ENVOYER.includes(o.etat));
    // État de départ de chaque opération : un envoi interrompu la rend tel quel (un conflit reste un conflit).
    const etatInitial = new Map(aEnvoyer.map((o) => [o.identifiantClient, o.etat === 'conflit' ? 'conflit' : 'en_attente_envoi'] as const));
    const remettre = async (o: OperationStockee) => this.file.changerEtat(o.identifiantClient, etatInitial.get(o.identifiantClient) ?? 'en_attente_envoi', o.motif, {});
    for (let i = 0; i < aEnvoyer.length; i += this.tailleLot) {
      const candidats = aEnvoyer.slice(i, i + this.tailleLot);
      const lot: OperationStockee[] = [];
      const envoi: OperationEnvoi[] = [];
      for (const o of candidats) {
        let charge: unknown;
        try {
          charge = await this.file.charge(o.identifiantClient);
        } catch {
          // Charge indéchiffrable (clé de session perdue à la fermeture de l'application) : l'enregistrement est
          // refusé avec son motif, pour que les suivants partent et que la personne le ressaisisse.
          await this.file.changerEtat(o.identifiantClient, 'refuse', MOTIF_CHARGE_ILLISIBLE, { accuseLe: this.maintenant().toISOString(), code: 'charge_illisible' });
          bilan.refusees++;
          continue;
        }
        const preparation = this.preparations.get(o.type);
        if (preparation) {
          try {
            charge = await preparation(charge, o);
          } catch (e) {
            if (e instanceof ErreurTransport) {
              // Préparation impossible sans le serveur (photos à envoyer) : rien ne part, tout reste en attente.
              for (const x of lot) await remettre(x);
              bilan.interrompu = e.message;
              if (e.sessionPerdue) this.oublierSession();
              await this.rafraichir({ derniereErreur: bilan.interrompu });
              return bilan;
            }
            await this.file.changerEtat(o.identifiantClient, 'refuse', e instanceof Error ? e.message : 'Préparation de l’envoi impossible', { accuseLe: this.maintenant().toISOString() });
            bilan.refusees++;
            continue;
          }
        }
        lot.push(o);
        envoi.push({
          identifiantClient: o.identifiantClient,
          type: o.type,
          charge,
          horodatageEvenement: o.horodatageEvenement,
          horodatageLocal: o.horodatageLocal,
          versionObjet: o.versionObjet ?? null,
        });
        await this.file.changerEtat(o.identifiantClient, 'envoye', null, { envoyeLe: this.maintenant().toISOString() });
      }
      await this.rafraichir();
      if (lot.length === 0) continue;
      let resultats: ResultatServeur[];
      try {
        resultats = await this.transport.envoyer(envoi);
      } catch (e) {
        // Aucune réponse : rien n'est présenté comme reçu ; les opérations repartent en attente d'envoi.
        for (const o of lot) await remettre(o);
        bilan.interrompu = e instanceof Error ? e.message : 'Envoi interrompu';
        if (e instanceof ErreurTransport && e.sessionPerdue) this.oublierSession();
        await this.rafraichir({ derniereErreur: bilan.interrompu });
        return bilan;
      }
      bilan.envoyees += lot.length;
      const accuse = this.maintenant().toISOString();
      for (const r of resultats) {
        const o = lot.find((x) => x.identifiantClient === r.identifiantClient);
        if (!o) continue;
        if (r.etat === 'deja_recue' && r.resolution) {
          // Conflit tranché par un validateur : l'enregistrement sort du conflit avec la décision pour motif.
          const retenue = r.resolution.versionRetenue === 'client' ? 'votre version a été retenue' : 'la version du serveur a été retenue';
          await this.file.changerEtat(o.identifiantClient, 'synchronise', `Conflit tranché par un validateur, ${retenue} : ${r.resolution.motif}`, { accuseLe: accuse, conflitId: null, resultat: r.resultat ?? null });
          bilan.synchronisees++;
        } else if (r.etat === 'acceptee' || (r.etat === 'deja_recue' && !r.conflitId && !r.code)) {
          await this.file.changerEtat(o.identifiantClient, 'synchronise', null, { accuseLe: accuse, resultat: r.resultat ?? r.objet ?? null });
          bilan.synchronisees++;
        } else if (r.etat === 'conflit' || (r.etat === 'deja_recue' && r.conflitId)) {
          await this.file.changerEtat(o.identifiantClient, 'conflit', r.motif ?? 'Conflit avec une autre saisie', { accuseLe: accuse, conflitId: r.conflitId ?? null, resultat: r.versions ?? null });
          bilan.conflits++;
        } else {
          await this.file.changerEtat(o.identifiantClient, 'refuse', r.motif ?? 'Opération refusée par le serveur', { accuseLe: accuse, code: r.code ?? null });
          bilan.refusees++;
        }
      }
      // Une opération sans réponse dans le lot reste à renvoyer.
      for (const o of lot) {
        if (!resultats.some((r) => r.identifiantClient === o.identifiantClient)) await remettre(o);
      }
    }
    const fin = this.maintenant().toISOString();
    this.stockage.setItem(CLE_DERNIERE, fin);
    await this.purgerRefus();
    await this.rafraichir({ derniereSynchro: fin });
    return bilan;
  }

  /** Refus conservés 30 jours pour consultation, puis retirés (cadrage, section 9). */
  async purgerRefus(): Promise<number> {
    const limite = this.maintenant().getTime() - CONSERVATION_REFUS_JOURS * 24 * H;
    let n = 0;
    for (const o of await this.file.lister()) {
      const reference = Date.parse(o.accuseLe ?? o.horodatageLocal);
      if ((o.etat === 'refuse' || o.etat === 'synchronise') && reference < limite) {
        await this.file.retirer(o.identifiantClient);
        n++;
      }
    }
    return n;
  }

  // --- Paquet de travail ---------------------------------------------------------------------------------------

  /** Télécharge le paquet de travail si sa version a changé ; le conserve chiffré sur l'appareil. */
  async actualiserPaquet(): Promise<{ version: string; inchange: boolean }> {
    const local = await this.file.lireDonnee<Record<string, unknown>>(CLE_PAQUET);
    const p = await this.transport.paquet(local?.version ?? null);
    if (p.inchange && local) {
      await this.rafraichir({ paquetVersion: p.version, paquetLe: local.enregistreLe });
      return { version: p.version, inchange: true };
    }
    if (!p.contenu) {
      // Version identique mais copie locale illisible : on la redemande en entier.
      const complet = await this.transport.paquet(null);
      await this.file.ecrireDonnee(CLE_PAQUET, complet.contenu ?? {}, complet.version);
      await this.rafraichir({ paquetVersion: complet.version, paquetLe: new Date().toISOString() });
      return { version: complet.version, inchange: false };
    }
    await this.file.ecrireDonnee(CLE_PAQUET, p.contenu, p.version);
    await this.rafraichir({ paquetVersion: p.version, paquetLe: new Date().toISOString() });
    return { version: p.version, inchange: false };
  }

  /** Dernières données synchronisées de l'utilisateur (consultation hors connexion). */
  async paquet<T = Record<string, unknown>>(): Promise<{ version: string | null; enregistreLe: string; contenu: T } | null> {
    return this.file.lireDonnee<T>(CLE_PAQUET);
  }

  // --- Cycle de vie ----------------------------------------------------------------------------------------------

  /**
   * Démarre la reprise : vérifie l'intégrité de la file, synchronise à l'ouverture, puis à chaque retour du
   * réseau. Renvoie une fonction d'arrêt.
   */
  async demarrer(cible: Pick<Window, 'addEventListener' | 'removeEventListener'> | null = typeof window === 'undefined' ? null : window): Promise<() => void> {
    const integrite = await this.file.verifierIntegrite();
    await this.rafraichir({ perdus: integrite.perdus });
    const surRetour = () => {
      this.publier({ enLigne: true });
      void this.synchroniser()
        .then(() => this.actualiserPaquet())
        .catch(() => undefined);
    };
    const surPerte = () => this.publier({ enLigne: false });
    cible?.addEventListener('online', surRetour);
    cible?.addEventListener('offline', surPerte);
    this.nettoyage = () => {
      cible?.removeEventListener('online', surRetour);
      cible?.removeEventListener('offline', surPerte);
    };
    if (this.estEnLigne()) surRetour();
    return () => this.arreter();
  }

  arreter(): void {
    this.nettoyage?.();
    this.nettoyage = null;
  }

  /** L'utilisateur a pris acte de la perte d'enregistrements locaux. */
  async prendreActeDeLaPurge(): Promise<void> {
    await this.file.prendreActeDeLaPurge();
    await this.rafraichir({ perdus: [] });
  }

  etat(): EtatSynchro {
    return this.etatCourant;
  }

  abonner(ecouteur: (e: EtatSynchro) => void): () => void {
    this.ecouteurs.add(ecouteur);
    ecouteur(this.etatCourant);
    return () => this.ecouteurs.delete(ecouteur);
  }

  private publier(partiel: Partial<EtatSynchro>): void {
    this.etatCourant = { ...this.etatCourant, ...partiel };
    for (const e of this.ecouteurs) e(this.etatCourant);
  }

  private async rafraichir(partiel: Partial<EtatSynchro> = {}): Promise<void> {
    const compteurs: EtatSynchro['compteurs'] = { enregistre_local: 0, en_attente_envoi: 0, envoye: 0, synchronise: 0, conflit: 0, refuse: 0 };
    for (const o of await this.file.lister()) compteurs[o.etat]++;
    this.publier({ ...partiel, compteurs, enLigne: partiel.enLigne ?? this.estEnLigne() });
  }
}
